import { BuildSimpleTable } from './build-simple-table-processing';
import { mapValues } from '@vydaps/gui-commons/records';
import { getFullTableRect, TableOverlayLine } from '../table-overlay/table-overlay';
import { ROW_MERGE_DISTANCE_THRESHOLD } from './build-simple-close-row-merging';
import { LineOrientation } from '../models/interfaces';
import { signal } from '@angular/core';
import { OcrService } from '../service/ocr/ocr.service';
import { getLocatedMemberEntries } from './build-simple-row-access';

/**
 * Note: This function MUTATES the input by changing signal values inside of `tables[i].overlayData`
 *
 * Note: This function does some nasty `O(n^2)` stuff, use it only with small™ input values.
 *
 * Since [6dcd13](https://github.com/VYDAPS/ps-gui/commit/6dcd13a2710f3ebdbce9cb26db48da09a396cbaf)
 * we only use build simple to get the structure of the table and then use our own logic to extract
 * the actual values of the table by its rectangular structure. This fixes some of the issues caused
 * by the fact that build simple does not extract actual tables but only its cells on field level,
 * but implies that columns which are far apart from each other create "too wide columns" whose
 * content then leaks into the table data.
 *
 * This function checks for each adjacent column pair C1 and C2 if there are any words fully between
 * those columns. By fully between we mean that a word is strict to the right (of the right bound)
 * of C1 and strict to the left (of the left bound) of C2. Then, a new column marked as ignored
 * ('Unwichtig') is inserted between those columns.
 *
 * Examples:
 *
 * ```
 * ----------------
 * | C1        C2 |
 * | C1        C2 |
 * ```
 *
 * -> No column added since there are no words fully between C1 and C2.
 *
 * ```
 * -------------------------------
 * | C___1  some_text    C___2   |
 * | C___1          C___2        |
 * -------------------------------
 * ```
 *
 * -> No column added since there are no words fully between C1 and C2
 *    some_text intuitively should form a column, but its bounds touch C___2 in the second row
 *    and thus no column is added.
 *
 * ```
 * ------------------------------------------
 * | C___1               some_text    C___2 |
 * | C___1                            C___2 |
 * | C__ 1  other text                C___2 |
 * ```
 *
 * -> Exactly one new column is inserted between C1 and C2. We do _not_ run a table recognition
 *    algorithm on this structure (no magic wand here) but simply combine all words fully inbetween
 *    into one column.
 */
export const fillTableColumnGapsWithIgnoredColumns = (
  tables: BuildSimpleTable[],
  ocrService: OcrService,
) => {
  tables.forEach((bsTable) => {
    const columnHeaders = bsTable.overlayData
      .columnHeaders()
      .filter((header) => header !== undefined);

    const extractedFieldsByColumn = getExtractedFieldsByColumn(bsTable);

    const fullTableRect = getFullTableRect(bsTable.overlayData);

    const newIgnoredColumns: { index: number; leftOffset: number }[] = [];
    for (let i = -1; i < columnHeaders.length; i++) {
      const getCurrentMaxRight = (columnIndex: number) => {
        if (columnIndex === -1) return fullTableRect.x[0];

        return Math.max(
          ...extractedFieldsByColumn[columnHeaders[columnIndex]].map(
            ({ location }) => location.right,
          ),
        );
      };

      const getFollowingMinLeft = (columnIndex: number) => {
        if (columnIndex === columnHeaders.length - 1) return fullTableRect.x[1];

        return Math.min(
          ...extractedFieldsByColumn[columnHeaders[columnIndex + 1]].map(
            ({ location }) => location.left,
          ),
        );
      };

      const currentMaxRight = getCurrentMaxRight(i);
      const followingMinLeft = getFollowingMinLeft(i);

      if (Math.abs(currentMaxRight - followingMinLeft) < ROW_MERGE_DISTANCE_THRESHOLD) {
        continue;
      }

      const wordsBetweenColumns = ocrService.getWordsInRect(bsTable.overlayData.pageId, {
        x: [currentMaxRight, followingMinLeft],
        y: fullTableRect.y,
      });

      // There are non-extracted-words between the right boundary of the current column and the
      // left boundary of the following column, thus we want to include a new ignored column
      // to prevent its data to leak into the next column.
      if (wordsBetweenColumns.length > 0) {
        newIgnoredColumns.push({
          index: i + 1,
          leftOffset: followingMinLeft,
        });
      }
    }

    addIgnoredColumns(bsTable, newIgnoredColumns);
  });
};

const getExtractedFieldsByColumn = (bsTable: BuildSimpleTable) =>
  mapValues(
    Object.groupBy(
      bsTable.originalRows.flatMap((row) => getLocatedMemberEntries(row)),
      ([key, _]) => key,
    ),
    (_, value) => value.map(([, cell]) => ({ value: cell.value, location: cell.location })),
  );

const addIgnoredColumns = (
  table: BuildSimpleTable,
  newIgnoredColumns: { index: number; leftOffset: number }[],
) => {
  table.overlayData.lines.vertical.update((previousLines) => {
    const updatedLines: TableOverlayLine[] = [...previousLines];
    for (let i = 0; i < newIgnoredColumns.length; i++) {
      const newIgnoredColumn = newIgnoredColumns[i];
      updatedLines.splice(newIgnoredColumn.index + 1 + i, 0, {
        isBoundary: false,
        orientation: LineOrientation.VERTICAL,
        offset: signal(newIgnoredColumn.leftOffset),
      });
    }
    return updatedLines;
  });

  table.overlayData.columnHeaders.update((oldHeaders) => {
    const updatedHeaders = [...oldHeaders];
    for (let i = 0; i < newIgnoredColumns.length; i++) {
      const newIgnoredColumn = newIgnoredColumns[i];
      updatedHeaders.splice(newIgnoredColumn.index + i, 0, 'Unwichtig');
    }
    return updatedHeaders;
  });
};
